// src/components/ui/multi-select.tsx (or your chosen path)
import { Check, ChevronsUpDown, X } from "lucide-react";
import * as React from "react";

import { Badge } from "~/components/ui/badge"; // Adjust path
import { Button } from "~/components/ui/button"; // Adjust path
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "~/components/ui/command"; // Adjust path
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover"; // Adjust path
import { cn } from "~/lib/utils"; // Adjust path as needed

export type MultiSelectOption = {
    /** The value used internally and returned in onChange */
    value: string | number;
    /** The human-readable label displayed */
    label: string;
    /** Optional icon component */
    icon?: React.ComponentType<{ className?: string }>;
};

interface MultiSelectProps {
    /** Array of available options */
    options: MultiSelectOption[];
    /** Array of currently selected values */
    selected: (string | number)[];
    /** Callback function when the selection changes */
    onChange: (selected: (string | number)[]) => void;
    /** Additional CSS class name for the trigger button */
    className?: string;
    /** Placeholder text when no items are selected */
    placeholder?: string;
    /** Placeholder text for the search input */
    searchPlaceholder?: string;
    /** Text for the "No results found" message */
    emptyText?: string;
    /** Text for the "Clear selection" command */
    clearText?: string;
    /** Text for the "Select all" command */
    selectAllText?: string;
    /** Maximum number of items to display in the list before scrolling */
    maxDisplayedItems?: number;
    /** Whether the component is disabled */
    disabled?: boolean;
    /** Aria label for the trigger button */
    ariaLabel?: string;
}

export function MultiSelect({
    options,
    selected,
    onChange,
    className,
    placeholder = "Select options...",
    searchPlaceholder = "Search...",
    emptyText = "No results found.",
    clearText = "Clear selection",
    selectAllText = "Select all",
    maxDisplayedItems = 5, // Default max items shown before scroll
    disabled = false,
    ariaLabel = "Select options",
    ...props
}: MultiSelectProps) {
    const [open, setOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");

    const selectedOptions = React.useMemo(() =>
        options.filter(option => selected.includes(option.value)),
        [options, selected]
    );

    const handleUnselect = React.useCallback((itemValue: string | number) => {
        onChange(selected.filter((v) => v !== itemValue));
    }, [selected, onChange]);

    const handleSelect = React.useCallback((itemValue: string | number) => {
        onChange([...selected, itemValue]);
    }, [selected, onChange]);

    const handleClear = React.useCallback(() => {
        onChange([]);
        // Optionally close the popover after clearing
        // setOpen(false);
    }, [onChange]);

    const handleSelectAll = React.useCallback(() => {
        onChange(options.map(option => option.value));
        // Optionally close the popover after selecting all
        // setOpen(false);
    }, [options, onChange]);

    // Helper function for keyboard interaction on the remove icon
    const handleKeyDownRemove = (
        e: React.KeyboardEvent<HTMLSpanElement>,
        value: string | number
    ) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleUnselect(value);
        }
    };

    const allSelected = options.length > 0 && selected.length === options.length;
    const noneSelected = selected.length === 0;

    const listHeight = `max-h-[${maxDisplayedItems * 2}rem]`;

    return (
        <Popover open={open} onOpenChange={setOpen} {...props}>
            <PopoverTrigger asChild disabled={disabled}>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-label={ariaLabel}
                    className={cn(
                        "w-full justify-between min-h-10 h-auto",
                        className
                    )}
                    // Remove onClick here if it conflicts with PopoverTrigger's internal handling
                    // onClick={() => setOpen(!open)} // PopoverTrigger manages this
                    disabled={disabled}
                >
                    <div className="flex flex-wrap items-center gap-1">
                        {selectedOptions.length > 0 ? (
                            selectedOptions.map((option) => (
                                <Badge
                                    key={option.value}
                                    variant="secondary"
                                    className="px-2 py-0.5"
                                    // Prevent badge click from toggling popover if needed, though span click takes precedence
                                    // onClick={(e) => e.stopPropagation()}
                                >
                                    <span className="mr-1">{option.label}</span>
                                    {/* FIX: Use a span with button role instead of a nested button */}
                                    <span
                                        role="button"
                                        tabIndex={disabled ? -1 : 0} // Make it focusable unless disabled
                                        aria-label={`Remove ${option.label}`}
                                        onKeyDown={(e) => handleKeyDownRemove(e, option.value)} // Handle Enter/Space press
                                        onClick={(e) => {
                                            // No need for preventDefault as it's not a submittable element
                                            e.stopPropagation(); // Crucial: Prevent click from toggling the Popover
                                            if (!disabled) {
                                                handleUnselect(option.value);
                                            }
                                        }}
                                        // Add focus styles and cursor, handle disabled state visually
                                        className={cn(
                                            "ml-0.5 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2",
                                            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                        )}
                                        // aria-disabled is important for screen readers
                                        aria-disabled={disabled}
                                    >
                                        <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                    </span>
                                </Badge>
                            ))
                        ) : (
                            <span className="text-muted-foreground">{placeholder}</span>
                        )}
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onValueChange={setSearchTerm}
                    />
                    <CommandList className={listHeight}>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                             {/* Action items */}
                            {(options.length > 0 && (selectAllText || clearText)) && (
                                <>
                                    {selectAllText && (
                                        <CommandItem
                                            onSelect={handleSelectAll}
                                            disabled={allSelected || disabled}
                                            className="text-xs justify-center opacity-80 cursor-pointer"
                                            aria-label={selectAllText}
                                        >
                                            {selectAllText}
                                        </CommandItem>
                                    )}
                                    {clearText && (
                                        <CommandItem
                                            onSelect={handleClear}
                                            disabled={noneSelected || disabled}
                                            className="text-xs justify-center opacity-80 cursor-pointer"
                                            aria-label={clearText}
                                        >
                                            {clearText}
                                        </CommandItem>
                                    )}
                                    <CommandSeparator />
                                </>
                            )}
                            {/* Options */}
                            {options
                                .filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
                                .map((option) => {
                                const isSelected = selected.includes(option.value);
                                return (
                                    <CommandItem
                                        key={option.value}
                                        value={`${option.label}-${option.value}`}
                                        disabled={disabled}
                                        onSelect={() => {
                                            if (!disabled) {
                                                if (isSelected) {
                                                    handleUnselect(option.value);
                                                } else {
                                                    handleSelect(option.value);
                                                }
                                            }
                                        }}
                                        className="cursor-pointer"
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4",
                                                isSelected ? "opacity-100" : "opacity-0"
                                            )}
                                        />
                                        {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                                        <span>{option.label}</span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}